import React, { useCallback, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, Divider, Grid } from '@mui/material';
import { DialogHeading } from 'components/dialog-title';
import { useController, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslate } from 'hooks/use-translate';
import { useEffectError, useFieldProps } from 'hooks';
import { convertToDate, dateFormat } from 'utils/dates';
import { apiUserPatientPrescriptions } from 'services/user-patient-profile-prescriptions';
import { APP_FORMAT_DATE_TIME } from 'configs/const';
import { apiUserPatientProfile } from 'services/user-patient-profile';
import { apiPdf } from 'services/pdf';
import { useAppSelector } from 'store';
import { selectLanguageID } from 'store/languages';
import { isMutationFulfilled } from 'utils/rtk-query';
import { AppPrescriptionDrugsAreaInput } from 'components/app-prescription-drugs-area-input';
import { AppChipAreaInput } from 'components/app-chip-area-input';
import style from './index.module.scss';
import { BaseCreateComponentProps } from '../../models';
import { apiPdfContent } from 'services/pdf-content';
import { DialogEditHtmlContent } from 'components/dialog-edit-html-content';
import { apiClinicalMeetings } from 'services/clinical-meetings';
import { AppInputMeetingListSelect } from 'components/app-inpit-meeting-list';

const schema = yup.object({
  meeting: yup
    .object({
      id: yup.string().required(),
      date: yup.string().required('rule-required').default(''),
    })
    .required('rule-required')
    .default(null),
  drugs: yup
    .array()
    .of(
      yup.object({
        id: yup.string(),
        title: yup.string(),
      }),
    )
    .min(1, 'rule-required')
    .default([]),
});
const defaultValues = schema.cast({}, { assert: false });
type FormModel = typeof defaultValues;

type ApplyState = { title: string; content: string };

const useFetchClinicalMeetingsQuery =
  apiClinicalMeetings.useGetClinicalMeetingForEndOfTreatmentConsultationQuery;

const useFetchPrescriptionsQuery =
  apiUserPatientPrescriptions.useGetMedicalPrescriptionsWithDrugsQuery;

const useFetchPatientDetailsLazyQuery =
  apiUserPatientProfile.useLazyGetPatientDetailsForPdfSummaryOfConsultationDrugsHeQuery;

const useGenerateContentMutation =
  apiPdfContent.useGenerateContentSummaryOfConsultationDrugsHeMutation;

interface Props {
  userPatientProfileID: string;
  onApply: (state: ApplyState) => void;
  onClose: () => void;
}
const DialogCreateContent: React.FC<React.PropsWithChildren<Props>> = ({
  userPatientProfileID,
  onApply,
  onClose,
}) => {
  const languageID = useAppSelector(selectLanguageID);

  const [triggerFetchDetails, resultDetails] = useFetchPatientDetailsLazyQuery();
  useEffectError(resultDetails.error);

  const resultMeetings = useFetchClinicalMeetingsQuery(userPatientProfileID);
  useEffectError(resultMeetings.error);

  const meetings = useMemo(() => resultMeetings.data || [], [resultMeetings.data]);
  const meetingsList = useMemo(() => {
    return meetings.map((meeting) => {
      return {
        meetingFromDateTime: String(meeting.meetingFromDateTime),
        meetingToDateTime: String(meeting.meetingToDateTime),
        employee: meeting.employee,
        type: meeting.subject,
        id: String(meeting.id),
      };
    });
  }, [meetings]);

  const { tp, t } = useTranslate();
  const methods = useForm({ defaultValues, resolver: yupResolver(schema) });
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = methods;
  const getFieldProps = useFieldProps({ errors, emptyHelperText: '' });

  const controlMeeting = useController({ control, name: 'meeting' });

  const handleChangeMeeting = (id: string | undefined | null) => {
    if (id === 'today') {
      controlMeeting.field.onChange({ id: 'today', date: new Date().toISOString() });
      return;
    }

    const meeting = meetingsList.find((meeting) => meeting.id === id);
    if (!meeting) return;

    controlMeeting.field.onChange({ id: meeting.id, date: meeting.meetingFromDateTime });
  };

  const valueMeeting = controlMeeting.field.value?.id;

  const [generateContent, resultContent] = useGenerateContentMutation();
  useEffectError(resultContent.error);

  const resultPrescriptions = useFetchPrescriptionsQuery(userPatientProfileID);
  useEffectError(resultPrescriptions.error);

  const prescriptions = useMemo(() => resultPrescriptions.data || [], [resultPrescriptions.data]);

  const prescriptionOptions = useMemo(() => {
    return prescriptions.map((prescription) => {
      return {
        id: String(prescription.id),
        title: dateFormat(prescription.entryDate, APP_FORMAT_DATE_TIME),
        isCompleted: !!prescription.isCompleted,
        entryDate: convertToDate(prescription.entryDate),
        items: prescription.drugs.map((drug) => {
          return { id: String(drug.id), title: String(drug.drugNameEng) };
        }),
      };
    });
  }, [prescriptions]);

  const isLoading =
    resultContent.isLoading ||
    resultDetails.isLoading ||
    resultPrescriptions.isLoading ||
    resultMeetings.isLoading;
  const controlDrugs = useController({ control, name: 'drugs' });

  const _onSubmit = useCallback(
    async (data: FormModel) => {
      const { drugs, meeting } = data;

      const resultDetails = await triggerFetchDetails({
        userPatientProfileID,
      });

      if (!resultDetails.data) {
        return;
      }

      const result = await generateContent({
        languageID,
        payload: {
          patient: resultDetails.data,
          drugs: drugs.map(({ title }) => ({ drugName: String(title) || 'unknown-drug' })),
          date: meeting.date,
        },
      });

      if (isMutationFulfilled(result)) {
        onApply({ content: result.data.content, title: result.data.title });
      }
    },
    [triggerFetchDetails, userPatientProfileID, languageID, generateContent, onApply],
  );

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth={'lg'}>
      <DialogHeading
        title={tp('create-summary-of-consultation-drugs-he')}
        isLoading={isLoading}
        onClose={onClose}
      />
      <DialogContent dividers className={style.dialogContent}>
        <AppInputMeetingListSelect
          label={t('select-meeting')}
          error={!!errors.meeting}
          // FieldError objects reach helperText unchanged (latent, RHF v6); typed through for the RHF phase (D-05)
          helperText={(errors.meeting?.date || errors.meeting?.id) as React.ReactNode}
          value={valueMeeting}
          onChange={handleChangeMeeting}
          options={meetingsList}
          disabled={isLoading}
        />
        <Divider className={style.divider} />
        <Grid container spacing={2}>
          <Grid
            size={{
              md: 7,
            }}
          >
            <AppPrescriptionDrugsAreaInput
              {...getFieldProps(controlDrugs.field)}
              label={t('prescriptions')}
              options={prescriptionOptions}
              disabled={isLoading}
            />
          </Grid>
          <Grid
            size={{
              md: 5,
            }}
          >
            <AppChipAreaInput {...getFieldProps(controlDrugs.field)} disabled={isLoading} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color={'primary'} onClick={onClose}>
          {tp('cancel')}
        </Button>
        <Button
          color={'primary'}
          variant={'contained'}
          disabled={isLoading}
          onClick={handleSubmit(_onSubmit)}
        >
          {tp('apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useGenerateDocumentMutation = apiPdf.useGenerateDocumentSummaryOfConsultationDrugsHeMutation;

export const DialogCreateDocSummaryOfConsultationDrugsHe: React.FC<
  React.PropsWithChildren<BaseCreateComponentProps>
> = ({ userPatientProfileID, onDone, onClose }) => {
  const [state, setState] = useState<ApplyState>();

  const [triggerGenerate, resultGenerate] = useGenerateDocumentMutation();
  useEffectError(resultGenerate.error);

  const isLoading = resultGenerate.isLoading;

  const onGenerate = useCallback(
    async (content: string) => {
      if (!state) return;
      const res = await triggerGenerate({ content, title: state.title, userPatientProfileID });

      if (isMutationFulfilled(res)) {
        onDone();
      }
    },
    [triggerGenerate, onDone, userPatientProfileID, state],
  );

  if (!state) {
    return (
      <DialogCreateContent
        userPatientProfileID={userPatientProfileID}
        onApply={setState}
        onClose={onClose}
      />
    );
  }

  return (
    <DialogEditHtmlContent
      isLoading={isLoading}
      title={state.title}
      content={state.content}
      onGenerate={onGenerate}
      onCancel={onClose}
    />
  );
};
